import { useEffect, useState } from "react"
import { BsChevronDown, BsPlayCircle, BsCheckCircleFill } from "react-icons/bs"
import { FiStar, FiArrowLeft } from "react-icons/fi"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"

export default function VideoDetailsSidebar({ setReviewModal, setSidebarOpen }) {
  const [activeSection, setActiveSection] = useState("")
  const [activeSubSection, setActiveSubSection] = useState("")
  const navigate = useNavigate()
  const location = useLocation()
  const { sectionId, subSectionId } = useParams()
  const { courseSectionData, courseEntireData, totalNoOfLectures, completedLectures } =
    useSelector((state) => state.viewCourse)

  useEffect(() => {
    if (!courseSectionData.length) return
    const secIdx = courseSectionData.findIndex((d) => d._id === sectionId)
    if (secIdx === -1) return
    const subIdx = courseSectionData[secIdx]?.subSection.findIndex(
      (d) => d._id === subSectionId
    )
    setActiveSection(courseSectionData[secIdx]?._id)
    setActiveSubSection(courseSectionData[secIdx]?.subSection?.[subIdx]?._id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSectionData, courseEntireData, location.pathname])

  const progressPct = totalNoOfLectures
    ? Math.round((completedLectures.length / totalNoOfLectures) * 100)
    : 0

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-richblack-700 p-4">
        <div className="mb-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/dashboard/enrolled-courses")}
            className="flex items-center gap-1.5 text-xs font-medium text-richblack-400 transition-colors hover:text-richblack-5"
          >
            <FiArrowLeft size={13} />
            Dashboard
          </button>
          <button
            onClick={() => setReviewModal(true)}
            className="flex items-center gap-1 text-xs font-medium text-yellow-50 transition-opacity hover:opacity-75"
          >
            <FiStar size={12} />
            Rate
          </button>
        </div>

        <p className="line-clamp-2 text-sm font-semibold leading-snug text-richblack-5">
          {courseEntireData?.courseName}
        </p>

        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-richblack-400">
            {completedLectures?.length} / {totalNoOfLectures} completed
          </span>
          <span className="font-semibold text-caribbeangreen-300">{progressPct}%</span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-richblack-700">
          <div
            className="h-full rounded-full bg-caribbeangreen-300 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* ── Section list ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto py-1">
        {courseSectionData.map((section, idx) => {
          const completedInSection = section.subSection.filter((s) =>
            completedLectures.includes(s._id)
          ).length
          const isOpen = activeSection === section._id

          return (
            <div key={idx} className="border-b border-richblack-700/60 last:border-0">
              {/* Section header */}
              <button
                onClick={() => setActiveSection(isOpen ? "" : section._id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-richblack-700/40"
              >
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-xs font-semibold leading-snug text-richblack-5">
                    {section.sectionName}
                  </p>
                  <p className="mt-0.5 text-[11px] text-richblack-500">
                    {completedInSection}/{section.subSection.length} &middot;{" "}
                    {section.subSection.length} lecture
                    {section.subSection.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <BsChevronDown
                  size={13}
                  className={`flex-shrink-0 text-richblack-500 transition-transform duration-300 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Lectures */}
              {isOpen && (
                <div className="pb-1">
                  {section.subSection.map((topic, i) => {
                    const isActive = activeSubSection === topic._id
                    const isDone = completedLectures.includes(topic._id)
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          navigate(
                            `/view-course/${courseEntireData?._id}/section/${section?._id}/sub-section/${topic?._id}`
                          )
                          setActiveSubSection(topic._id)
                          // close sidebar only on mobile
                          if (window.innerWidth < 768) setSidebarOpen(false)
                        }}
                        className={`flex w-full items-start gap-3 px-4 py-2.5 text-left text-xs transition-colors ${
                          isActive
                            ? "bg-yellow-50/10 text-yellow-50"
                            : "text-richblack-300 hover:bg-richblack-700/40 hover:text-richblack-5"
                        }`}
                      >
                        <div className="mt-0.5 flex-shrink-0">
                          {isDone ? (
                            <BsCheckCircleFill
                              size={13}
                              className="text-caribbeangreen-300"
                            />
                          ) : (
                            <BsPlayCircle
                              size={13}
                              className={isActive ? "text-yellow-50" : "text-richblack-600"}
                            />
                          )}
                        </div>
                        <span
                          className={`line-clamp-2 leading-snug ${
                            isActive ? "font-medium" : ""
                          }`}
                        >
                          {topic.title}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
